// The review lease the open document is under (ADR-0009), from the screen's side: ask for it when
// a document opens, renew it every 30 s, give it back on leaving, and know who holds the others so
// the spine can say so. The channel and the reviewer come from the bridge Paige installed.
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import {
  ReviewLeaseRefused,
  type ReviewHolder,
  type ReviewLease,
  type ReviewLeaseChannel,
} from './review-leases';
import {
  onReviewConflict,
  rememberReviewVersion,
  type ReviewConflictError,
} from './review-versions';
import { readOnlyView } from './read-only';

export const HEARTBEAT_MS = 30_000;

export interface ReviewLeaseState {
  /**
   * `off` on a channel with no queue (setup, the tenant's channel); `asking` while Paige answers;
   * `viewing` in a read-only view, which reads every document and takes no lease.
   */
  status: 'off' | 'asking' | 'mine' | 'held' | 'viewing';
  /** Who holds the open document when it is not mine. */
  holder: ReviewHolder | null;
  /** When Take over becomes allowed, epoch ms; null while nobody holds it or it is mine. */
  takeOverAt: number | null;
  /** A save Paige refused: the document changed, or somebody else holds it. */
  conflict: ReviewConflictError | null;
  /** Other reviewers' live leases on the job, by document id, for the spine. */
  held: ReadonlyMap<string, ReviewHolder>;
  /** The document to open next: the first candidate nobody holds; null when they are all taken. */
  next: (candidates: readonly string[]) => Promise<string | null>;
  takeOver: () => Promise<void>;
  dismissConflict: () => void;
}

/** What Paige last said about a document: the lease, and whether the call that said it made it mine. */
interface Standing {
  documentId: string;
  lease: ReviewLease | null;
  mine: boolean;
}

type Apply = (lease: ReviewLease | null, documentId: string, mine: boolean) => void;

/** @param documentId the open document; @param onConflict reloads it after a refused save. */
export function useReviewLease(
  documentId: string | null,
  onConflict: (documentId: string) => void,
): ReviewLeaseState {
  const { channel, me, leasing } = bridgeLeases();
  const { standing, apply, granted } = useStanding();
  const held = useHeldByOthers(channel, me, documentId, standing);
  const conflict = useConflicts(documentId, apply, onConflict);
  useLeaseLifecycle(leasing, documentId, apply, granted);
  const { status, theirs } = viewingOr(channel, leasing, standingOf(channel, documentId, standing));
  useHeartbeat(leasing, documentId, status, apply);
  const next = useCallback(
    (candidates: readonly string[]) =>
      channel && !leasing ? peekOf(channel, candidates) : nextOf(channel, candidates, apply),
    [channel, leasing, apply],
  );
  const takeOver = useCallback(async () => {
    if (!leasing || !documentId) return;
    await takeOverOf(leasing, documentId, apply);
    // Theirs until a moment ago: show what they left, not what I opened.
    conflict.reload(documentId);
  }, [leasing, documentId, apply, conflict]);
  return {
    status,
    holder: theirs?.holder ?? null,
    takeOverAt: theirs?.takeOverAt ? Date.parse(theirs.takeOverAt) : null,
    conflict: conflict.current,
    held,
    next,
    takeOver,
    dismissConflict: conflict.dismiss,
  };
}

/** What the open document is under, from the last answer about it; `theirs` is the lease while a colleague holds it. */
function standingOf(
  channel: ReviewLeaseChannel | null,
  documentId: string | null,
  standing: Standing | null,
): { status: ReviewLeaseState['status']; theirs: ReviewLease | null } {
  if (!channel) return { status: 'off', theirs: null };
  const current = standing?.documentId === documentId ? standing : null;
  if (!current) return { status: 'asking', theirs: null };
  return current.mine
    ? { status: 'mine', theirs: null }
    : { status: 'held', theirs: current.lease };
}

/** The document to open next: Paige's answer on a job's channel, the first candidate elsewhere. */
async function nextOf(
  channel: ReviewLeaseChannel | null,
  candidates: readonly string[],
  apply: Apply,
): Promise<string | null> {
  if (!channel) return candidates[0] ?? null;
  const lease = await channel.next(candidates);
  if (lease) apply(lease, lease.documentId, true);
  return lease?.documentId ?? null;
}

/**
 * The bridge's lease channel, who I am, and the channel to lease on: none in a read-only view, which
 * reads who holds what and never asks for, renews or gives back a lease.
 */
function bridgeLeases(): {
  channel: ReviewLeaseChannel | null;
  me: string | null;
  leasing: ReviewLeaseChannel | null;
} {
  const channel = window.paigeWorkbench?.reviewLeases ?? null;
  const me = window.paigeWorkbench?.user?.id ?? null;
  return { channel, me, leasing: readOnlyView() ? null : channel };
}

/** A read-only view on a job's channel is `viewing`, whatever the leases say; anyone else's standing as it is. */
function viewingOr(
  channel: ReviewLeaseChannel | null,
  leasing: ReviewLeaseChannel | null,
  standing: { status: ReviewLeaseState['status']; theirs: ReviewLease | null },
): { status: ReviewLeaseState['status']; theirs: ReviewLease | null } {
  return channel && !leasing ? { status: 'viewing', theirs: null } : standing;
}

/** A read-only view's next: the first candidate nobody holds, with no lease taken. */
async function peekOf(
  channel: ReviewLeaseChannel,
  candidates: readonly string[],
): Promise<string | null> {
  return (await channel.peek(candidates))?.documentId ?? null;
}

/** The last answer about a document, and the document granted to me (a ref, read by the effects). */
function useStanding(): {
  standing: Standing | null;
  apply: Apply;
  granted: RefObject<string | null>;
} {
  const [standing, setStanding] = useState<Standing | null>(null);
  const granted = useRef<string | null>(null);
  const apply = useCallback<Apply>((lease, documentId, mine) => {
    const ours = mine && lease !== null;
    granted.current = ours ? documentId : null;
    if (ours) rememberReviewVersion(documentId, lease.reviewVersion);
    setStanding({ documentId, lease, mine: ours });
  }, []);
  return { standing, apply, granted };
}

/** Opening a document asks for it; leaving (another document, the route, the tab) gives it back. */
function useLeaseLifecycle(
  channel: ReviewLeaseChannel | null,
  documentId: string | null,
  apply: Apply,
  granted: RefObject<string | null>,
): void {
  useEffect(() => {
    if (!channel || !documentId) return undefined;
    // `next` already granted this one on the way here; anything else is asked for now.
    if (granted.current !== documentId) void acquire(channel, documentId, apply);
    const leave = () => void channel.release(documentId, true);
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('pagehide', leave);
      if (granted.current === documentId) granted.current = null;
      void channel.release(documentId, true);
    };
  }, [channel, documentId, apply, granted]);
}

/** Every 30 s: renew mine, or ask again for one a colleague held (they may have finished). */
function useHeartbeat(
  channel: ReviewLeaseChannel | null,
  documentId: string | null,
  status: ReviewLeaseState['status'],
  apply: Apply,
): void {
  useEffect(() => {
    if (!channel || !documentId || status === 'off' || status === 'asking') return undefined;
    const beat = status === 'mine' ? renew : acquire;
    const timer = window.setInterval(() => void beat(channel, documentId, apply), HEARTBEAT_MS);
    return () => window.clearInterval(timer);
  }, [channel, documentId, status, apply]);
}

/** Other reviewers' live leases, refreshed when the open document changes and on every heartbeat. */
function useHeldByOthers(
  channel: ReviewLeaseChannel | null,
  me: string | null,
  documentId: string | null,
  standing: Standing | null,
): ReadonlyMap<string, ReviewHolder> {
  const [held, setHeld] = useState<ReadonlyMap<string, ReviewHolder>>(new Map());
  useEffect(() => {
    if (!channel || !documentId) return undefined;
    const refresh = () =>
      channel.list().then(
        (leases) => setHeld(othersOf(leases, me)),
        () => undefined,
      );
    void refresh();
    const timer = window.setInterval(() => void refresh(), HEARTBEAT_MS);
    return () => window.clearInterval(timer);
    // `standing` re-runs the refresh when my own lease moved, so the spine agrees with the fields column.
  }, [channel, me, documentId, standing]);
  return held;
}

/** A refused save on the open document: kept until dismissed; a held refusal also names the holder. */
function useConflicts(
  documentId: string | null,
  apply: Apply,
  onConflict: (documentId: string) => void,
): {
  current: ReviewConflictError | null;
  dismiss: () => void;
  reload: (documentId: string) => void;
} {
  const [conflict, setConflict] = useState<ReviewConflictError | null>(null);
  const latest = useRef(onConflict);
  useEffect(() => {
    latest.current = onConflict;
  }, [onConflict]);
  useEffect(
    () =>
      onReviewConflict((refused) => {
        if (refused.lease.documentId !== documentId) return;
        setConflict(refused);
        if (refused.code === 'review_held') apply(refused.lease, documentId, false);
        latest.current(documentId);
      }),
    [documentId, apply],
  );
  const current = conflict?.lease.documentId === documentId ? conflict : null;
  const dismiss = useCallback(() => setConflict(null), []);
  const reload = useCallback((id: string) => latest.current(id), []);
  return { current, dismiss, reload };
}

/** Asks for the one document; a colleague's lease or a network fault leaves it read-only. */
async function acquire(
  channel: ReviewLeaseChannel,
  documentId: string,
  apply: Apply,
): Promise<void> {
  try {
    const lease = await channel.next([documentId]);
    if (lease) return apply(lease, documentId, true);
    const standing = (await channel.list()).find((l) => l.documentId === documentId) ?? null;
    apply(standing, documentId, false);
  } catch {
    apply(null, documentId, false);
  }
}

/** Renews my lease; once it lapsed or was taken over, the document is theirs. */
async function renew(channel: ReviewLeaseChannel, documentId: string, apply: Apply): Promise<void> {
  try {
    apply(await channel.heartbeat(documentId), documentId, true);
  } catch (reason) {
    if (reason instanceof ReviewLeaseRefused) apply(reason.lease, documentId, false);
    // A network fault keeps the screen as it is; the next beat tries again.
  }
}

/** Takes a stale lease; while it is live the refusal says who still has it. */
async function takeOverOf(
  channel: ReviewLeaseChannel,
  documentId: string,
  apply: Apply,
): Promise<void> {
  try {
    apply(await channel.takeOver(documentId), documentId, true);
  } catch (reason) {
    if (reason instanceof ReviewLeaseRefused) apply(reason.lease, documentId, false);
    else throw reason;
  }
}

/** The live leases that are not mine, by document. */
function othersOf(leases: readonly ReviewLease[], me: string | null): Map<string, ReviewHolder> {
  const held = new Map<string, ReviewHolder>();
  for (const lease of leases)
    if (lease.holder && lease.holder.userId !== me) held.set(lease.documentId, lease.holder);
  return held;
}
